import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function Calculator() {
  const [form, setForm] = useState({
    niche: "Fitness",
    platform: "instagram",
    followers: 25000,
    avg_views: 8000,
    engagement_rate: 4.2,
    audience_location: "United States",
    content_type: "reel",
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const update = (k) => (v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/calc/rate", {
        ...form,
        followers: Number(form.followers),
        avg_views: Number(form.avg_views),
        engagement_rate: Number(form.engagement_rate),
      });
      setResult(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Calculation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-16">
      <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-3">// free · shareable</p>
      <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mb-3">
        What am I worth?
      </h1>
      <p className="text-neutral-400 mb-12 max-w-2xl">
        AI rate estimate based on niche, engagement, and audience. No login needed. Screenshot it, DM it,
        quote it.
      </p>

      <div className="grid md:grid-cols-2 gap-8">
        <form onSubmit={submit} className="space-y-4" data-testid="calc-form">
          <FormRow label="Niche">
            <Input
              value={form.niche}
              onChange={(e) => update("niche")(e.target.value)}
              className="h-12 bg-[#0e0e0e] border-white/10"
              placeholder="e.g. Fitness, Beauty, Tech"
              data-testid="calc-niche-input"
            />
          </FormRow>
          <FormRow label="Platform">
            <Select value={form.platform} onValueChange={update("platform")}>
              <SelectTrigger className="h-12 bg-[#0e0e0e] border-white/10" data-testid="calc-platform-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="tiktok">TikTok</SelectItem>
                <SelectItem value="youtube">YouTube</SelectItem>
              </SelectContent>
            </Select>
          </FormRow>
          <FormRow label="Content type">
            <Select value={form.content_type} onValueChange={update("content_type")}>
              <SelectTrigger className="h-12 bg-[#0e0e0e] border-white/10" data-testid="calc-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="reel">Reel / Short</SelectItem>
                <SelectItem value="post">Static post</SelectItem>
                <SelectItem value="story">Story frame</SelectItem>
                <SelectItem value="video">Long-form video</SelectItem>
              </SelectContent>
            </Select>
          </FormRow>
          <div className="grid grid-cols-2 gap-4">
            <FormRow label="Followers">
              <Input
                type="number"
                value={form.followers}
                onChange={(e) => update("followers")(e.target.value)}
                className="h-12 bg-[#0e0e0e] border-white/10 font-mono"
                data-testid="calc-followers-input"
              />
            </FormRow>
            <FormRow label="Avg views">
              <Input
                type="number"
                value={form.avg_views}
                onChange={(e) => update("avg_views")(e.target.value)}
                className="h-12 bg-[#0e0e0e] border-white/10 font-mono"
                data-testid="calc-views-input"
              />
            </FormRow>
          </div>
          <FormRow label="Engagement rate (%)">
            <Input
              type="number"
              step="0.1"
              value={form.engagement_rate}
              onChange={(e) => update("engagement_rate")(e.target.value)}
              className="h-12 bg-[#0e0e0e] border-white/10 font-mono"
              data-testid="calc-er-input"
            />
          </FormRow>
          <FormRow label="Primary audience location">
            <Input
              value={form.audience_location}
              onChange={(e) => update("audience_location")(e.target.value)}
              className="h-12 bg-[#0e0e0e] border-white/10"
              data-testid="calc-location-input"
            />
          </FormRow>
          <Button
            type="submit"
            disabled={busy}
            className="w-full h-12 rounded-full bg-[#CCFF00] text-black hover:bg-[#b8e600] font-semibold hover:scale-[1.02] transition-transform"
            data-testid="calc-submit-btn"
          >
            {busy ? "Crunching numbers…" : "Estimate my rate"} <Sparkles className="w-4 h-4" />
          </Button>
        </form>

        <div className="p-8 rounded-2xl bg-[#141414] border border-white/5 min-h-[400px]" data-testid="calc-result-panel">
          {!result && !busy && (
            <p className="text-neutral-500 text-sm">Your rate card appears here.</p>
          )}
          {busy && <p className="text-neutral-400 text-sm animate-pulse">AI is calculating…</p>}
          {result && (
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-2">
                // your rate card
              </p>
              <div className="grid grid-cols-3 gap-2 mt-6" data-testid="calc-result-numbers">
                <RateCol label="Low" value={result.low} />
                <RateCol label="Mid" value={result.mid} accent />
                <RateCol label="High" value={result.high} />
              </div>
              <p className="mt-8 text-sm text-neutral-300 leading-relaxed">{result.reasoning}</p>
              {result.tips?.length > 0 && (
                <ul className="mt-6 space-y-2">
                  {result.tips.map((t, i) => (
                    <li key={i} className="text-sm text-neutral-400 flex gap-2">
                      <span className="text-[#CCFF00]">◆</span> {t}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const FormRow = ({ label, children }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest font-mono text-neutral-500 mb-1 block">{label}</span>
    {children}
  </label>
);

const RateCol = ({ label, value, accent }) => (
  <div className={`p-4 rounded-xl ${accent ? "bg-[#CCFF00] text-black" : "bg-[#0e0e0e] border border-white/5"}`}>
    <p className={`text-[10px] font-mono uppercase tracking-widest ${accent ? "text-black/60" : "text-neutral-500"}`}>
      {label}
    </p>
    <p className="font-display font-black text-3xl mt-1 tracking-tight">${value?.toLocaleString?.() ?? value}</p>
  </div>
);
